import { action, observable, runInAction } from 'mobx';

import { checklistController } from './checklistController';
import {
  ChecklistItem,
  CreateChecklistRequest,
  UpdateChecklistRequest,
  ChecklistItemExtended,
  AddedItem,
} from '../../api/checklistApi';

export interface ChecklistStore {
  checklist: ChecklistItem[],
  extendedChecklist: ChecklistItemExtended[],
  orderId: string,
  error: any,
}

export const checklistStore: ChecklistStore = observable({
  checklist: [],
  orderId: undefined,
  error: null,
} as ChecklistStore);

export const checklistActions = {

  getChecklist: async (orderId:string): Promise<void> => {
    checklistStore.orderId = orderId;
    const response = await checklistController.getChecklist(orderId);
    checklistActions.handleError(response);
    runInAction(() => {
      checklistStore.checklist = response.data.items;
    });
  },

  prepareExtendedChecklist: async (ar: AddedItem[], orderId): Promise<void> => {
    const { checklist } = checklistStore;
    const x = ar.map((v) => {
      const y = checklist.find((k) => k.contentRefValueId === v.id);
      return {
        checked: null, orderId, ...y, contentRefValueId: v.id, contentRefValueName: v.name, updated: false,
      };
    });
    runInAction(() => {
      checklistStore.extendedChecklist = x;
    });
  },

  prepareExtendedChecklistWithoutOrder: async (ar: AddedItem[]): Promise<void> => {
    // const { checklist } = checklistStore;
    const x = ar.map((v) => {
      // const y = checklist.find((k) => k.contentRefValueId === v.id);
      return {
        checked: null, orderId: null, contentRefValueId: v.id, contentRefValueName: v.name, updated: false,
      };
    });
    runInAction(() => {
      checklistStore.extendedChecklist = x;
    });
  },

  createChecklist: async (request: CreateChecklistRequest): Promise<void> => {
    const response = await checklistController.createChecklist(request);
    checklistActions.handleError(response);
  },

  updateChecklist: async (request: UpdateChecklistRequest): Promise<void> => {
    const response = await checklistController.updateChecklist(request);
    checklistActions.handleError(response);
  },

  deleteChecklistItem: async (id: string): Promise<void> => {
    const response = await checklistController.deleteChecklist(id);
    checklistActions.handleError(response);
  },

  clearChecklist: action(() => {
    checklistStore.checklist = [];
    checklistStore.extendedChecklist = [];
    checklistStore.orderId = null;
  }),

  clearError: action(() => {
    checklistStore.error = null;
  }),

  handleError: action((response) => {
    if (response.errorData) {
      const { errorData } = response;
      checklistStore.error = errorData;
      throw errorData;
    }
  }),

  setChecklistItem: action((refValueId:number, value: boolean) => {
    const { extendedChecklist } = checklistStore;
    const x = [...extendedChecklist];
    x.find((v) => v.contentRefValueId === refValueId).checked = value;
    x.find((v) => v.contentRefValueId === refValueId).updated = true;
    checklistStore.extendedChecklist = x;
  }),

  clearChecklistUpdated: action(() => {
    checklistStore.extendedChecklist = checklistStore.extendedChecklist
      .map((v) => { return { ...v, updated: false }; });
  }),

  saveChecklist: async (id?: string): Promise<void> => {
    if (checklistStore.extendedChecklist) {
      const promiseInsert = checklistStore.extendedChecklist
        .filter((v) => v.updated && !v.id && (v.checked === false || v.checked === true))
        .map((v) => checklistActions.createChecklist({
          orderId: v.orderId ?? id,
          contentRefValueId: v.contentRefValueId,
          checked: v.checked,
        }));
      await Promise.all(promiseInsert);

      const promiseUpdate = checklistStore.extendedChecklist
        .filter((v) => v.updated && v.id && (v.checked === false || v.checked === true))
        .map((v) => checklistActions.updateChecklist({
          id: v.id,
          checked: v.checked,
        }));
      await Promise.all(promiseUpdate);

      const promisesDeleted = checklistStore.extendedChecklist
        .filter((v) => v.updated && (v.checked === null) && !!v.id)
        .map((v) => checklistActions.deleteChecklistItem(v.id));
      await Promise.all(promisesDeleted);

      checklistActions.clearChecklistUpdated();
    }
  },

  isChecklistUpdated: (): boolean => {
    if (checklistStore.extendedChecklist && checklistStore.extendedChecklist.find((v) => v.updated === true)) {
      return true;
    }
    return false;
  },

};
